import React, { useState, useContext } from 'react';

import { Flex } from '@circleci/compass';
import { useRouter } from 'next/router';
import { useCookies } from 'react-cookie';
import { useIntl } from 'react-intl';

import { HeaderWithBanner } from '~/components/HeaderWithBanner/HeaderWithBanner';
import { NewsletterSignup } from '~/components/NewsletterSignup';
import {
  mediaTabletAndLarger,
  mediaLargeAndLarger,
  mediaHugeAndLarger,
  containerWidthTabletAndLarger,
  containerWidthLargeAndLarger,
  containerWidthHugeAndLarger,
} from '~/components/shared/viewPorts';
import BannerContext from '~/contexts/BannerContext';
import { LibProvider } from '~/contexts/LibContext';
import { MeContext } from '~/contexts/MeContext';
import { useAnalytics } from '~/hooks/useAnalytics';
import { useTrackCLID } from '~/hooks/useTrackCLID';
import { styled } from '~/styled';
import {
  pathToSearchState,
  searchStateToPath,
} from '~/utils/algoliaSearchState/algoliaSearchState';

import { Footer } from '../Footer';

interface Props {
  children: React.ReactNode;
  withoutNewsletterSection?: boolean;
}

// leave enBanner & jaBanner empty if you don't want a banner
export const enBanner = '';
export const jaBanner = '';

const FooterWrapper = styled.footer`
  padding-bottom: 35px;
  ${mediaLargeAndLarger} {
    padding-bottom: 40px;
  }
`;

const FooterContentWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0 15px;
  ${mediaTabletAndLarger} {
    width: ${containerWidthTabletAndLarger}px;
  }
  ${mediaLargeAndLarger} {
    width: ${containerWidthLargeAndLarger}px;
  }
  ${mediaHugeAndLarger} {
    width: ${containerWidthHugeAndLarger}px;
  }
`;

const HeaderWrapper = styled(Flex)(({ theme }) => {
  return `
    width: 100%;
    // box-shadow: 0 -6px 18px -3px #000000bd; // not in theme
    z-index: 100; // needs to be on top of everything (except for the TopSheet)
    height: 126px;
    // background-color: ${theme.colors.white};
    // position: fixed;
    top: 0px;
  `;
});

const Presentation = ({ children, withoutNewsletterSection }: Props) => {
  // Implenting hook to temporarily track and store fbclid and gclid
  // For more info: https://circleci.atlassian.net/browse/DD-806
  useTrackCLID();
  const [isBannerClosing, setIsClosing] = useState(false);
  const [cookies, setCookie] = useCookies(['bannerDevhubClosed']);

  const intl = useIntl();
  const { trackStandardizedAction } = useAnalytics();
  const { me } = useContext(MeContext);
  const router = useRouter();

  const bannerToClose = cookies.bannerDevhubClosed;
  const currentBanner = intl.locale === 'en' ? enBanner : jaBanner;
  const initialSearchState = pathToSearchState(router.asPath);

  const [showCurrentBanner, setShowCurrentBanner] = useState(
    !!currentBanner && bannerToClose != currentBanner,
  );

  const handleBannerClose = () => {
    setIsClosing(true);
    setCookie('bannerDevhubClosed', currentBanner, { path: '/developer' });
    setTimeout(() => {
      return setShowCurrentBanner(false);
    }, 500);
  };

  return (
    <LibProvider
      platformName="Developer"
      user={!!me}
      notifyTrackingFired={trackStandardizedAction}
      initialSearchState={initialSearchState}
      onSearchStateChange={(searchState) => {
        const newUrl = searchStateToPath(router.asPath, searchState);
        router.replace(newUrl, undefined, {
          shallow: true,
        });
      }}
    >
      <BannerContext.Provider
        value={{
          currentBanner: currentBanner,
          handleBannerClose: handleBannerClose,
          showCurrentBanner: showCurrentBanner,
          isBannerClosing: isBannerClosing,
        }}
      >
        <HeaderWrapper>
          <HeaderWithBanner />
        </HeaderWrapper>
        {children}
        {withoutNewsletterSection ? null : <NewsletterSignup />}
        <FooterWrapper>
          <FooterContentWrapper>
            <Footer />
          </FooterContentWrapper>
        </FooterWrapper>
      </BannerContext.Provider>
    </LibProvider>
  );
};

export default Presentation;
